import { useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

type DashboardRefreshControlProps = {
  lastUpdatedLabel: string;
  refreshing: boolean;
  onRefresh: () => Promise<void>;
};

// "Last updated" pill with a refresh button — the only way to refetch
// dashboard data besides logging in again.
export const DashboardRefreshControl = ({ lastUpdatedLabel, refreshing, onRefresh }: DashboardRefreshControlProps) => {
  const [refreshedJustNow, setRefreshedJustNow] = useState(false);

  const handleRefresh = async () => {
    await onRefresh();
    setRefreshedJustNow(true);
    setTimeout(() => setRefreshedJustNow(false), 1500);
  };

  const label = refreshing ? "Refreshing…" : refreshedJustNow ? "Updated just now" : `Last updated: ${lastUpdatedLabel}`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        pl: 1.5,
        pr: 0.5,
        py: 0.25,
        borderRadius: 999,
        border: "1px solid #e5e7eb",
        bgcolor: "#ffffff",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: refreshedJustNow ? "success.main" : "#94a3b8",
          transition: "background-color 0.2s ease",
        }}
      />
      <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "#475569", ml: 0.5 }}>{label}</Typography>

      <Tooltip title="Refresh">
        <span>
          <IconButton size="small" onClick={handleRefresh} disabled={refreshing} aria-label="Refresh" sx={{ color: "#475569" }}>
            <RefreshIcon
              sx={{
                fontSize: 18,
                animation: refreshing ? "dashboard-refresh-spin 0.8s linear infinite" : "none",
                "@keyframes dashboard-refresh-spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
